import { type ReactNode, createContext, useContext, useState, useCallback } from "react";

type MobileNavbarContextType = {
  title?: string | false;
  position?: "top" | "bottom";
  navbarChildren?: ReactNode;
  showBackButton?: boolean;
  setMobileNavbar: (config: Omit<MobileNavbarContextType, "setMobileNavbar">) => void;
};

const MobileNavbarContext = createContext<MobileNavbarContextType | undefined>(undefined);

export function MobileNavbarProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<string | false | undefined>(undefined);
  const [position, setPosition] = useState<"top" | "bottom">("bottom");
  const [navbarChildren, setNavbarChildren] = useState<ReactNode | undefined>(undefined);
  const [showBackButton, setShowBackButton] = useState<boolean>(false);

  const setMobileNavbar = useCallback(
    ({
      title: newTitle,
      position: newPosition,
      navbarChildren: newChildren,
      showBackButton: newShowBackButton,
    }: Omit<MobileNavbarContextType, "setMobileNavbar">) => {
      setTitle(newTitle);
      setPosition(newPosition ?? "bottom");
      setNavbarChildren(newChildren);
      setShowBackButton(newShowBackButton ?? false);
    },
    [], // No dependencies, so the function reference remains stable
  );

  return (
    <MobileNavbarContext.Provider
      value={{
        title,
        position,
        navbarChildren,
        showBackButton,
        setMobileNavbar,
      }}
    >
      {children}
    </MobileNavbarContext.Provider>
  );
}

export function useMobileNavbar(): MobileNavbarContextType {
  const context = useContext(MobileNavbarContext);

  // At this point, isMobile is true. If context is undefined for mobile, throw error.
  if (context === undefined) {
    throw new Error("useMobileNavbar must be used within a MobileNavbarProvider");
  }

  // Context is guaranteed to be defined here for mobile
  return context;
}
